import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, Star, Trash2, X } from 'lucide-react';
import api from '../lib/api';
import { publicImageUrl } from '../lib/media';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { keyed } from '../lib/listKeys';

const MAX_PHOTOS = 4;

/**
 * Small-square-thumbnail photo uploader, capped at `max` images, with an
 * "add" tile while under the cap. The first photo is the cover image.
 * Clicking a thumbnail opens a lightbox with zoom + "Set as cover".
 */
export const MultiImageUploadField = ({
  value, onChange, label, endpoint, fields, testId, disabled = false, onBusyChange, max = MAX_PHOTOS,
}) => {
  const input = useRef(null);
  const busyCallback = useRef(onBusyChange);
  busyCallback.current = onBusyChange;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const photos = value || [];

  useEffect(() => () => busyCallback.current?.(false), []);

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) { setError('Choose a PNG, JPEG or WebP image.'); return; }
    if (!file.size || file.size > 5 * 1024 * 1024) { setError('Choose an image up to 5 MB.'); return; }
    if (photos.length >= max) { setError(`Up to ${max} photos allowed.`); return; }
    setBusy(true); busyCallback.current?.(true);
    try {
      const body = new FormData(); body.append('file', file);
      Object.entries(fields || {}).forEach(([key, entry]) => { if (entry !== undefined && entry !== null && entry !== '') body.append(key, String(entry)); });
      const result = await api.post(endpoint, body, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange([...photos, result.data.url]);
    } catch (e) {
      setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not upload this photo.');
    } finally {
      setBusy(false); busyCallback.current?.(false);
    }
  };

  const remove = (index) => { onChange(photos.filter((_, i) => i !== index)); setLightboxIndex(null); };
  const setAsCover = (index) => { onChange([photos[index], ...photos.filter((_, i) => i !== index)]); setLightboxIndex(0); };

  return (
    <div className="space-y-2" data-testid={`${testId}-field`}>
      <label className="text-sm font-medium">{label}</label>
      <div className="flex flex-wrap gap-2">
        {keyed(photos).map(({ item: url, key }, i) => (
          <div key={key} className="relative w-16 h-16 rounded-lg border overflow-hidden group" data-testid={`${testId}-thumb-${i}`}>
            <button type="button" className="w-full h-full block cursor-zoom-in" onClick={() => setLightboxIndex(i)} data-testid={`${testId}-zoom-${i}`}>
              <img src={publicImageUrl(url)} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
            </button>
            <button type="button" disabled={disabled || busy}
              onClick={() => remove(i)}
              className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
              data-testid={`${testId}-remove-${i}`}>
              <X className="h-3 w-3" />
            </button>
            {i === 0 && <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[8px] text-center py-0.5">Cover</span>}
          </div>
        ))}
        {photos.length < max && (
          <button type="button" disabled={disabled || busy} onClick={() => input.current?.click()}
            className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center hover:border-slate-400 hover:bg-slate-50 transition-colors disabled:opacity-50"
            data-testid={`${testId}-add`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : <ImagePlus className="h-5 w-5 text-slate-400" />}
          </button>
        )}
      </div>
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={upload} disabled={busy || disabled} data-testid={`${testId}-file`} />
      <p className="text-xs text-muted-foreground" data-testid={`${testId}-requirements`}>PNG, JPEG or WebP · up to 5 MB each · {photos.length}/{max} photos. First photo is the cover image.</p>
      {error && <p role="alert" className="text-xs text-red-700" data-testid={`${testId}-error`}>{error}</p>}

      <Dialog open={lightboxIndex !== null} onOpenChange={(v) => !v && setLightboxIndex(null)}>
        <DialogContent className="max-w-lg" data-testid={`${testId}-lightbox`}>
          <DialogHeader><DialogTitle>Photo {lightboxIndex !== null ? lightboxIndex + 1 : ''} of {photos.length}</DialogTitle></DialogHeader>
          {lightboxIndex !== null && photos[lightboxIndex] && (
            <>
              <div className="rounded-lg overflow-hidden border bg-slate-50 flex items-center justify-center" style={{ maxHeight: '60vh' }}>
                <img src={publicImageUrl(photos[lightboxIndex])} alt={`Photo ${lightboxIndex + 1}`} className="max-h-[60vh] w-auto object-contain" data-testid={`${testId}-lightbox-image`} />
              </div>
              <div className="flex justify-between items-center pt-2">
                <Button type="button" variant="outline" size="sm" className="gap-1.5" disabled={disabled || lightboxIndex === 0}
                  onClick={() => setAsCover(lightboxIndex)} data-testid={`${testId}-lightbox-set-cover`}>
                  <Star className="h-3.5 w-3.5" /> {lightboxIndex === 0 ? 'Cover photo' : 'Set as cover'}
                </Button>
                <Button type="button" variant="outline" size="sm" className="gap-1.5 text-red-700" disabled={disabled}
                  onClick={() => remove(lightboxIndex)} data-testid={`${testId}-lightbox-remove`}>
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
